import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, ShieldAlert, ShieldX, ChevronDown, ChevronUp,
  CircleCheck, CircleAlert,
} from "lucide-react";

export default function GuardrailBadge({ guardrail }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  if (!guardrail || typeof guardrail.safety_score !== "number") return null;

  const { safety_score: score, safety_label: label, status, extracted, validation, hallucinations } = guardrail;
  const palette = label === "green"
    ? { stroke: "border-emerald-500/40", bg: "bg-emerald-500/5", text: "text-emerald-300", Icon: ShieldCheck }
    : label === "yellow"
      ? { stroke: "border-amber-500/40", bg: "bg-amber-500/5", text: "text-amber-300", Icon: ShieldAlert }
      : { stroke: "border-rose-500/40", bg: "bg-rose-500/5", text: "text-rose-300", Icon: ShieldX };
  const Icon = palette.Icon;
  const headline = status === "BLOCKED"
    ? t("guardrailBadge.blockedHeadline")
    : score >= 70 ? t("guardrailBadge.reliable")
    : score >= 35 ? t("guardrailBadge.partial")
    : t("guardrailBadge.manualCheck");

  const missing = Array.from(new Set([...(extracted?.missing_data || []), ...(validation?.missing || [])]));
  const halls = hallucinations || [];
  const caps = guardrail.caps_applied || [];
  const eq = guardrail.extraction_quality || {};

  return (
    <div className={`tech-card mb-6 ${palette.stroke} ${palette.bg}`} data-testid="quote-guardrail-badge">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full p-4 flex items-center gap-4 text-left"
        data-testid="guardrail-badge-toggle"
      >
        <div className={`w-12 h-12 rounded-md border ${palette.stroke} flex items-center justify-center ${palette.text} shrink-0`}>
          <Icon size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className={`label-tech ${palette.text} mb-0.5`}>{"// GUARDRAIL_SCORE"}</div>
          <div className="flex items-baseline gap-3">
            <span className={`font-display font-bold text-2xl ${palette.text}`}>{score}<span className="text-base text-slate-500">/100</span></span>
            <span className="text-sm text-slate-300 truncate">{headline}</span>
          </div>
          <div className="text-[10px] text-slate-500 font-mono-data mt-0.5">
            {missing.length > 0 ? t("guardrailBadge.missingN", { count: missing.length }) : t("guardrailBadge.allPresent")}
            {halls.length > 0 ? ` · ${t("guardrailBadge.hallucinationsN", { count: halls.length })}` : ""}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-700 bg-slate-800/60 text-slate-300 font-mono-data" data-testid="guardrail-pricing-mode">
              {t(`priceGate.${guardrail.pricing_mode || "exact"}Label`)}
            </span>
            {caps.includes("cap_raster_source") && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 font-mono-data" data-testid="guardrail-cap-raster">
                {t("guardrailBadge.capRaster")}
              </span>
            )}
            {caps.includes("cap_features_unconfirmed") && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border border-rose-500/40 bg-rose-500/10 text-rose-300 font-mono-data" data-testid="guardrail-cap-unconfirmed">
                {t("guardrailBadge.capUnconfirmed")}
              </span>
            )}
            {eq.confirmed && (
              <span className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/50 bg-emerald-500/10 text-emerald-300 font-mono-data" data-testid="guardrail-features-confirmed">
                {t("guardrailBadge.featuresConfirmed", { count: eq.user_confirmed_fields || 0 })}
              </span>
            )}
          </div>
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400 shrink-0"/> : <ChevronDown size={16} className="text-slate-400 shrink-0"/>}
      </button>

      {open && (
        <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm" data-testid="guardrail-badge-detail">
          <div className="rounded-md border border-emerald-500/30 p-3" data-testid="guardrail-detail-certain">
            <div className="flex items-center gap-2 mb-2 text-emerald-300">
              <CircleCheck size={12}/> <span className="label-tech text-[10px]">{t("guardrailBadge.certainData")}</span>
            </div>
            <ul className="space-y-1 text-[12px] font-mono-data text-emerald-200">
              <li>{t("guardrailBadge.material")}: <span className={extracted?.material ? "text-emerald-100" : "text-slate-500"}>{extracted?.material || "—"}</span></li>
              <li>{t("guardrailBadge.dimensions")}: <span className={extracted?.dimensions?.length ? "text-emerald-100" : "text-slate-500"}>{extracted?.dimensions?.length ? `${extracted.dimensions.join(" × ")} mm` : "—"}</span></li>
              <li>{t("guardrailBadge.tolerances")}: <span className="text-emerald-100">{extracted?.tolerances?.length || 0}</span></li>
              <li>{t("guardrailBadge.operations")}: <span className="text-emerald-100">{extracted?.operations?.length || 0}</span></li>
              <li>{t("guardrailBadge.tools")}: <span className="text-emerald-100">{extracted?.tools?.length || 0}</span></li>
            </ul>
          </div>

          <div className="rounded-md border border-rose-500/30 p-3" data-testid="guardrail-detail-missing">
            <div className="flex items-center gap-2 mb-2 text-rose-300">
              <CircleAlert size={12}/> <span className="label-tech text-[10px]">{t("guardrailBadge.missingData")}</span>
            </div>
            {missing.length === 0 ? (
              <p className="text-[12px] text-emerald-300">{t("guardrailBadge.noCriticalGaps")}</p>
            ) : (
              <ul className="space-y-1 text-[12px] font-mono-data text-rose-200">
                {missing.map((m) => (
                  <li key={`gm-${m}`}>· {m.replace(/_/g, " ")}</li>
                ))}
              </ul>
            )}
            {validation?.warnings?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-800 text-[11px] text-amber-200">
                {validation.warnings.map((w, i) => <p key={`vw-${i}`}>{w}</p>)}
              </div>
            )}
          </div>

          {halls.length > 0 && (
            <div className="md:col-span-2 rounded-md border border-amber-500/40 p-3" data-testid="guardrail-detail-hallucinations">
              <div className="flex items-center gap-2 mb-2 text-amber-300">
                <CircleAlert size={12}/> <span className="label-tech text-[10px]">{t("guardrailBadge.hallucinationsTitle", { count: halls.length })}</span>
              </div>
              <ul className="space-y-1 text-[11px] font-mono-data text-amber-200">
                {halls.slice(0, 5).map((h, i) => (
                  <li key={`gh-${i}`}>! {h.kind} · &quot;{h.quoted_value}&quot; <span className="text-slate-500">in {h.where}</span></li>
                ))}
                {halls.length > 5 && (
                  <li className="text-slate-500">{t("guardrailBadge.andMore", { count: halls.length - 5 })}</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
